'use client';

import { Flex, Typography } from '@coolblue-development/freddy';
import selectOrderTypeTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/selectOrderTypeTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import { SelectableBox, SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import type { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useContext, useState } from 'react';

import { LeaseContext } from './Context';
import { formatPrice, getLowestLeaseTermAmount } from './utils';

type OrderTypeProps = {
  product: Product;
};

const OrderTypeButtons = ({ product }: OrderTypeProps) => {
  const { t, Trans } = useTranslation('webshop.business.leasing.orderTypeButtons');
  const { trackEvent } = useAnalytics();
  const searchParams = useSearchParams();

  const [isLeasePage, setIsLeasePage] = useState(searchParams.get('lease') === '1');

  const { selectedLeaseAmount, leaseTerms, legacyLeaseAmount, isLegacyLeaseFlow } =
    useContext(LeaseContext);
  const leaseAmount = isLegacyLeaseFlow ? legacyLeaseAmount : selectedLeaseAmount;

  const isOutOfStock = product.availabilityState !== AvailabilityStates.OnStock;
  const isPreOrder = product.availabilityState === AvailabilityStates.PreOrder;

  const shouldDisableLeaseOption = isOutOfStock && !isPreOrder;

  const salesPriceExclVat = product.salesPrice.excludingVat;

  const router = useRouter();

  function handleToggleMode(lease: boolean) {
    setIsLeasePage(lease);

    trackEvent(
      selectOrderTypeTrigger({
        content: lease ? 'lease' : 'one-time'
      })
    );

    const params = new URLSearchParams(searchParams.toString());
    if (lease) {
      params.set('lease', '1');
    } else {
      params.delete('lease');
    }
    router.replace(`?${params.toString()}`, { scroll: false });
  }

  function renderOneTimeContent() {
    if (isLeasePage) {
      return (
        <>
          <Typography component='span' mr={100}>
            {t('oneTime')}
          </Typography>
          <Typography component='span' fontWeight={700}>
            {formatPrice(salesPriceExclVat)}
          </Typography>
        </>
      );
    }
    return t('oneTime');
  }

  function getLeasePriceText() {
    if (!isLegacyLeaseFlow && leaseTerms) {
      return t('startPrice', {
        price: formatPrice(getLowestLeaseTermAmount(leaseTerms))
      });
    }
    if (isLegacyLeaseFlow && legacyLeaseAmount) {
      return t('startPrice', {
        price: formatPrice(legacyLeaseAmount || 0)
      });
    }
    return null;
  }

  function renderLeaseContent() {
    if (isLeasePage) {
      return (
        <Typography component='span' mr={100}>
          {leaseAmount ? (
            <Trans
              components={{
                strong: <strong></strong>
              }}
              i18nKey={'leasePrice'}
              t={t}
              values={{
                price: formatPrice(leaseAmount)
              }}
            />
          ) : (
            t('leasen')
          )}
        </Typography>
      );
    }
    return (
      <>
        <Typography component='span' mr={100}>
          {t('leasen')}
        </Typography>
        <Typography component='span' fontWeight={700}>
          {getLeasePriceText()}
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography fontWeight={600} mr={100} mb={200}>
        {t('label')}
      </Typography>
      <Flex gap={200} mb={200} direction='row' alignItems='center'>
        <SelectableBoxGroup
          name='order-type-buttons'
          type='radio'
          spacing='compact'
          inputPlacement='hidden'
          value={isLeasePage ? 'lease' : 'one-time'}
        >
          <SelectableBox value='one-time' onClick={() => handleToggleMode(false)}>
            <Flex
              direction='row'
              alignItems='center'
              sx={{
                height: '20px'
              }}
            >
              {renderOneTimeContent()}
            </Flex>
          </SelectableBox>
          <SelectableBox
            value='lease'
            disabled={shouldDisableLeaseOption}
            onClick={() => {
              handleToggleMode(true);
            }}
          >
            <Flex
              direction='row'
              alignItems='center'
              sx={{
                height: '20px'
              }}
            >
              {renderLeaseContent()}
            </Flex>
          </SelectableBox>
        </SelectableBoxGroup>
      </Flex>
    </>
  );
};

export default OrderTypeButtons;
